import { useEffect, useRef, useState } from 'react';
import PanelSettings from './PanelSettings/PanelSettings';
import ComponentSettings from './Component/ComponentSettings';
import PreviewPanel from './Preview/PreviewPanel';
import MaterialBrowser from './MaterialBrowser/MaterialBrowser';
import { getPlainText } from './Preview/ColorParse';
import yaml from 'js-yaml';
import './Editor.scss';

import InventoryImg from '../../images/editor/icons/inventory.png'
import DialogImg from '../../images/editor/icons/dialog.png'
import FloodgateImg from '../../images/editor/icons/floodgate.png'

const isValidType = (type) => ['inventory', 'dialog', 'floodgate'].includes(type);

const getTabLabel = (panel, index) => {
  if (!panel?.title) return `Panel ${index + 1}`;
  const plain = getPlainText(String(panel.title));
  return plain.slice(0, 20) || `Panel ${index + 1}`;
};

export default function Editor({ textures, isLoading }) {
  const [leftOpen, setLeftOpen] = useState(true);

  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showNewConfigModal, setShowNewConfigModal] = useState(false);
  const [newPanelMode, setNewPanelMode] = useState(false);
  const [rawYamlPreview, setRawYamlPreview] = useState(false);
  const [exportedYaml, setExportedYaml] = useState('');
  const [importText, setImportText] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);

  const [deletedTab, setDeletedTab] = useState(null);
  const deletedTabTimerRef = useRef(null);

  // Material browser state
  const [showMaterialBrowser, setShowMaterialBrowser] = useState(false);
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialSearchMode, setMaterialSearchMode] = useState(false);
  // Holds the setter for whichever material field is currently focused
  const activeMaterialSetter = useRef(null);
  // True when the browser was opened automatically by a field focus (not the button)
  const browserAutoOpenedRef = useRef(false);

  // Multi-panel state
  const [panels, setPanels] = useState(() => {
    try {
      const saved = localStorage.getItem('editorPanels');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
      // DEPRECATED: migrate old single-panel users who haven't opened the editor since multi-panel launched.
      // Can be removed once fullConfig is no longer found in the wild.
      const legacy = localStorage.getItem('fullConfig');
      if (legacy) {
        const parsed = JSON.parse(legacy);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return [parsed];
      }
    } catch {}
    return [null];
  });
  const [activePanel, setActivePanel] = useState(0);

  // Derived active config
  const currentConfig = panels[activePanel] ?? null;
  const showTabs = panels.length > 1;

  // Measure the actual rendered height of the tabs bar (including its scrollbar when present)
  // so --tabs-height always exactly accounts for it in the preview height calc.
  const tabsRef = useRef(null);
  const [tabsHeight, setTabsHeight] = useState(() => showTabs ? 30 : 0);

  useEffect(() => {
    const el = tabsRef.current;
    if (!showTabs || !el) {
      setTabsHeight(0);
      return;
    }
    const update = () => setTabsHeight(el.offsetHeight + 2); // +2 for border-bottom
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, [showTabs]);

  // Save all panels to localStorage on change
  useEffect(() => {
    if (panels.length > 0 && panels[0] !== null) {
      localStorage.setItem('editorPanels', JSON.stringify(panels));
    }
  }, [panels]);

  // Show new config modal if active panel has no valid type (or no config at all, e.g. fresh private window)
  useEffect(() => {
    if (!currentConfig || !isValidType(currentConfig?.type)) {
      setNewPanelMode(false);
      setShowNewConfigModal(true);
    }
  }, [currentConfig]);

  const MAX_HISTORY = 50;
  // historySnapshotRef holds the config at the START of a typing burst so that
  // rapid keystrokes only produce one history entry rather than one per character.
  const historyDebounceRef = useRef(null);
  const historySnapshotRef = useRef(null);

  // Cancel any pending debounce without committing it to history.
  // Must be called before undo/redo/tab switches so a lingering timer from one
  // panel can't fire and corrupt a different panel's history.
  const flushHistoryDebounce = () => {
    clearTimeout(historyDebounceRef.current);
    historyDebounceRef.current = null;
    historySnapshotRef.current = null;
  };

  const updateConfig = (newConfig) => {
    // Snapshot before the first keystroke of this burst; subsequent keystrokes
    // just reset the timer so the snapshot stays anchored to the burst start.
    if (!historyDebounceRef.current && currentConfig) {
      historySnapshotRef.current = structuredClone(currentConfig);
    }
    clearTimeout(historyDebounceRef.current);
    // Commit the burst snapshot to history once the user pauses typing.
    // Guard against null in case currentConfig was null when the snapshot was taken.
    historyDebounceRef.current = setTimeout(() => {
      if (historySnapshotRef.current) {
        setHistory(prev => {
          const next = [...prev, historySnapshotRef.current];
          return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
        });
      }
      historySnapshotRef.current = null;
      historyDebounceRef.current = null;
    }, 500);

    setFuture([]);
    setPanels(prev => {
      const updated = [...prev];
      updated[activePanel] = structuredClone(newConfig);
      return updated;
    });
  };

  const undo = () => {
    // Discard any pending debounce so it can't fire after we change state.
    flushHistoryDebounce();
    // Filter nulls in case any slipped in — a stuck null would block all further undos.
    const validHistory = history.filter(h => h !== null);
    if (validHistory.length === 0) {
      if (validHistory.length !== history.length) setHistory(validHistory);
      return;
    }
    const previous = structuredClone(validHistory[validHistory.length - 1]);
    const newHistory = validHistory.slice(0, -1);
    if (currentConfig) setFuture(prev => [structuredClone(currentConfig), ...prev]);
    setHistory(newHistory);
    setPanels(prev => {
      const updated = [...prev];
      updated[activePanel] = previous;
      return updated;
    });
  };

  const redo = () => {
    flushHistoryDebounce();
    // Same null-filter defence as undo.
    const validFuture = future.filter(h => h !== null);
    if (validFuture.length === 0) {
      if (validFuture.length !== future.length) setFuture(validFuture);
      return;
    }
    const next = structuredClone(validFuture[0]);
    const newFuture = validFuture.slice(1);
    if (currentConfig) setHistory(prev => [...prev, structuredClone(currentConfig)]);
    setFuture(newFuture);
    setPanels(prev => {
      const updated = [...prev];
      updated[activePanel] = next;
      return updated;
    });
  };

  const switchTab = (index) => {
    if (index === activePanel) return;
    flushHistoryDebounce();
    setActivePanel(index);
    setSelectedItem(null);
    setHistory([]);
    setFuture([]);
  };

  const closeTab = (index, e) => {
    e.stopPropagation();
    flushHistoryDebounce();
    if (panels.length <= 1) return;
    const newPanels = panels.filter((_, i) => i !== index);

    let newActive;
    if (index === activePanel) {
      newActive = Math.min(index, newPanels.length - 1);
    } else if (index < activePanel) {
      newActive = activePanel - 1;
    } else {
      newActive = activePanel;
    }

    setDeletedTab({ panel: panels[index], index, prevActive: activePanel });
    clearTimeout(deletedTabTimerRef.current);
    deletedTabTimerRef.current = setTimeout(() => setDeletedTab(null), 5000);

    setPanels(newPanels);
    setActivePanel(newActive);

    if (index === activePanel) {
      setSelectedItem(null);
      setHistory([]);
      setFuture([]);
    }
  };

  const undoCloseTab = () => {
    if (!deletedTab) return;
    clearTimeout(deletedTabTimerRef.current);
    const { panel, index } = deletedTab;
    setPanels(prev => {
      const restored = [...prev];
      restored.splice(index, 0, panel);
      return restored;
    });
    setActivePanel(index);
    setSelectedItem(null);
    setHistory([]);
    setFuture([]);
    setDeletedTab(null);
  };

  const togglePanels = () => setLeftOpen(!leftOpen);

  const handleImport = () => {
    try {
      if (!importText || !importText.trim()) return;
      const parsed = yaml.load(importText.trim());

      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('YAML root must be an object');
      }

      const hasValidPanels = panels.some(p => isValidType(p?.type));
      if (hasValidPanels) {
        flushHistoryDebounce();
        const newIndex = panels.length;
        setPanels(prev => [...prev, structuredClone(parsed)]);
        setActivePanel(newIndex);
        setHistory([]);
        setFuture([]);
      } else {
        updateConfig(parsed);
      }
      setSelectedItem(null);
      setShowImportModal(false);
      setImportText('');
    } catch (e) {
      alert('Invalid YAML format or structure.');
    }
  };

  const handlePreviewChange = () => setRawYamlPreview(!rawYamlPreview);

  const handleExport = () => {
    const dumped = yaml.dump(currentConfig);
    setExportedYaml(dumped);
    setShowExportModal(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(exportedYaml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Called when a material field gains focus.
  // currentValue pre-populates the browser filter immediately without needing a keystroke.
  const handleMaterialFocus = (setter, currentValue) => {
    activeMaterialSetter.current = setter;
    setMaterialSearch(currentValue || '');
    setMaterialSearchMode(true);
    if (!showMaterialBrowser) {
      // Auto-open: remember this so blur can auto-close it again
      browserAutoOpenedRef.current = true;
      setShowMaterialBrowser(true);
    }
    // If already manually open, leave browserAutoOpenedRef.current as false
    // so blur doesn't auto-close it
  };

  // Delayed so a click on a browser item fires before mode clears.
  // If the browser was auto-opened by this field, close it on blur.
  const handleMaterialBlur = () => {
    setTimeout(() => {
      setMaterialSearchMode(false);
      activeMaterialSetter.current = null;
      if (browserAutoOpenedRef.current) {
        setShowMaterialBrowser(false);
        browserAutoOpenedRef.current = false;
      }
    }, 150);
  };

  const handleMaterialType = (value) => setMaterialSearch(value);

  // Called when the user clicks a material in linked mode
  const handleMaterialSelect = (materialName) => {
    if (activeMaterialSetter.current) {
      activeMaterialSetter.current(materialName);
      setMaterialSearch(materialName);
    }
  };

  const materialBrowserProps = {
    onMaterialFocus: handleMaterialFocus,
    onMaterialBlur: handleMaterialBlur,
    onMaterialType: handleMaterialType,
  };

  const typeImages = {
    inventory: InventoryImg,
    dialog: DialogImg,
    floodgate: FloodgateImg,
  };

  return (
    <div className="editor-container" style={{ '--tabs-height': `${tabsHeight}px` }}>
      {showTabs && (
        <div ref={tabsRef} className="editor-tabs">
          {panels.map((panel, i) => (
            <div
              key={i}
              className={`editor-tab${i === activePanel ? ' active' : ''}`}
              onClick={() => switchTab(i)}
            >
              <span>{getTabLabel(panel, i)}</span>
              <span className="tab-close" onClick={(e) => closeTab(i, e)}>×</span>
            </div>
          ))}
        </div>
      )}

      {currentConfig && <div className="editor-main">
        <div className={`left-panel ${leftOpen ? '' : 'collapsed'}`}>
          <div className="panel-header">Panel Settings</div>
          <div className="panel-content">
            <PanelSettings config={currentConfig} setConfig={updateConfig} selectedItem={selectedItem} setSelectedItem={setSelectedItem} />
          </div>
        </div>

        <div className="arrow-bar" onClick={togglePanels} aria-label="Toggle Panels">
          {leftOpen ? '◀' : '▶'}
        </div>

        <div className={`right-panel ${!leftOpen ? '' : 'collapsed'}`}>
          <div className="panel-header">Component Settings</div>
          <div className="panel-content">
            <ComponentSettings config={currentConfig} setConfig={updateConfig} selectedItem={selectedItem} setSelectedItem={setSelectedItem} materialBrowserProps={materialBrowserProps} />
          </div>
        </div>

        <div className="fixed-preview">
          <div className="panel-header">
            Main Preview
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
              <button className="history-button" onClick={undo} disabled={history.length === 0}>↩</button>
              <button className="history-button" onClick={redo} disabled={future.length === 0}>↪</button>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
              <button onClick={() => { setNewPanelMode(true); setShowNewConfigModal(true); }} className="new-project-button">Start New</button>
              <button
                className={`swap-button materials-button${showMaterialBrowser ? ' active' : ''}`}
                onClick={() => {
                  browserAutoOpenedRef.current = false;
                  setShowMaterialBrowser(p => !p);
                }}
                title="Material Browser"
              >
                ⛏
              </button>
              <button className="swap-button" onClick={handlePreviewChange}>▾</button>
              <button onClick={() => setShowImportModal(true)}>Import</button>
              <button onClick={handleExport}>Export</button>
            </div>
          </div>
          <div className="panel-content">
            <PreviewPanel rawYamlPreview={rawYamlPreview} config={currentConfig} setConfig={updateConfig} textures={textures} selectedItem={selectedItem} setSelectedItem={setSelectedItem} />
          </div>
          {showMaterialBrowser && (
            <MaterialBrowser
              textures={textures}
              onClose={() => {
                browserAutoOpenedRef.current = false;
                setShowMaterialBrowser(false);
              }}
              externalSearch={materialSearch}
              materialSearchMode={materialSearchMode}
              onMaterialSelect={handleMaterialSelect}
            />
          )}
        </div>
      </div>}

      {showImportModal && (
        <div className="modal" onClick={() => { setShowImportModal(false); if (!isValidType(currentConfig?.type)) setShowNewConfigModal(true); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Paste YAML Configuration</h3>

            {/* When v3 is phased out remove this warning */}
            <p style={{ color: "#ff9800" }}>This editor is for v4 Panels, the old editor for v3 panels is <a href="https://v3.commandpanels.net/editor">here</a></p>

            <textarea
              value={importText}
              onChange={e => setImportText(e.target.value)}
              placeholder="Paste YAML here"
              rows={10}
              style={{ width: '100%', resize: 'vertical' }}
            />
            <div style={{ marginTop: '10px' }}>
              <button onClick={handleImport}>Load</button>
              <button onClick={() => {
                setShowImportModal(false);
                if (!isValidType(currentConfig?.type)) setShowNewConfigModal(true);
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <div className="modal" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Exported YAML</h3>
            <textarea
              value={exportedYaml}
              readOnly
              rows={10}
              style={{ width: '100%', resize: 'vertical' }}
              onClick={copyToClipboard}
            />
            {copied && <p>Copied to clipboard!</p>}
            <div style={{ marginTop: '10px' }}>
              <button onClick={() => setShowExportModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showNewConfigModal && (
        <div className="modal" onClick={() => { if (isValidType(currentConfig?.type)) setShowNewConfigModal(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Choose a Panel Type</h3>
            <div className="new-config-options">
              {['inventory', 'dialog', 'floodgate'].map(type => (
                <div
                  key={type}
                  className="new-config-button"
                  onClick={() => {
                    if (newPanelMode) {
                      flushHistoryDebounce();
                      const newIndex = panels.length;
                      setPanels(prev => [...prev, { type }]);
                      setActivePanel(newIndex);
                      setHistory([]);
                      setFuture([]);
                      setSelectedItem(null);
                    } else {
                      updateConfig({ type });
                      setSelectedItem(null);
                    }
                    setShowNewConfigModal(false);
                    setNewPanelMode(false);
                  }}
                >
                  <img src={typeImages[type]} alt={type} />
                  <div>{type.charAt(0).toUpperCase() + type.slice(1)}</div>
                </div>
              ))}
            </div>
            
            {/* Maybe remove this after a little while when the editor is more mature */}
            <p>Learn the basics on how to use this editor <a href="https://youtu.be/i4TPitqmZ-E?si=vIMfVzQILcihCUy4">here</a></p>

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              {!isValidType(currentConfig?.type) ? (
                <button onClick={() => { setShowNewConfigModal(false); setShowImportModal(true); }}>Import Instead</button>
              ) : (
                <button onClick={() => setShowNewConfigModal(false)}>Cancel</button>
              )}
            </div>
          </div>
        </div>
      )}

      {deletedTab && (
        <div className="undo-snackbar">
          <span>Tab "{getTabLabel(deletedTab.panel, deletedTab.index)}" deleted</span>
          <button onClick={undoCloseTab}>Undo</button>
          <button className="undo-snackbar-dismiss" onClick={() => { clearTimeout(deletedTabTimerRef.current); setDeletedTab(null); }}>✕</button>
        </div>
      )}
    </div>
  );
}
